import React, { useState, useEffect } from 'react';
import './FragmentRedemption.css';
import PlayerService from '../../services/PlayerService';
import NotificationManager from '../../utils/NotificationManager';
import audioManager from '../../utils/filemanager/AudioManager';

// Display paths for file names (aesthetics only)
const FILE_DISPLAY_INFO = {
  'selenix_journal_fragment_01': { file: 'SELENIX_JOURNAL_FRAGMENT_01.TXT', path: '/IDCU-OS/LOGS/' },
  'selenix_journal_fragment_02': { file: 'SELENIX_JOURNAL_FRAGMENT_02.TXT', path: '/IDCU-OS/LOGS/' },
  'selenix_journal_fragment_03': { file: 'SELENIX_JOURNAL_FRAGMENT_03.TXT', path: '/IDCU-OS/LOGS/' },
  'selenix_personnel_file': { file: 'DR_AURELIA_SELENIX.TXT', path: '/IDCU-OS/PERSONNEL/LEADERSHIP/' },
  'classified_correspondence': { file: 'CLASSIFIED_CORRESPONDENCE.TXT', path: '/IDCU-OS/LOGS/REDACTED/' },
  'rom_entity_dossier': { file: 'ROM_ENTITY_DOSSIER.TXT', path: '/IDCU-OS/ENTITIES/' },
  'mf_entity_dossier': { file: 'MF_ENTITY_DOSSIER.TXT', path: '/IDCU-OS/ENTITIES/' },
  'x_entity_dossier': { file: 'X_ENTITY_DOSSIER.TXT', path: '/IDCU-OS/ENTITIES/' },
  'solis_personnel_file': { file: 'DR_VINCENT_M_SOLIS.TXT', path: '/IDCU-OS/PERSONNEL/LEADERSHIP/' },
  'incident_9172': { file: 'INCIDENT_9172.TXT', path: '/IDCU-OS/LOGS/REDACTED/' }
};

const getDisplayInfo = (fileName) => FILE_DISPLAY_INFO[fileName] || { file: fileName, path: '/IDCU-OS/' };

const FragmentRedemption = ({ onBack, linkedProfile }) => {
  const [fragmentKey, setFragmentKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [fragmentsRedeemed, setFragmentsRedeemed] = useState(0);
  const [totalFragments] = useState('?');
  const [pendingFragments, setPendingFragments] = useState([]);
  const [unlockedFiles, setUnlockedFiles] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [unlockedFilePath, setUnlockedFilePath] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [isDev] = useState(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const [isDefragging, setIsDefragging] = useState(false);
  const [defragProgress, setDefragProgress] = useState(0);
  const [defragStatus, setDefragStatus] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showTransmissionModal, setShowTransmissionModal] = useState(false);
  const [transmissionMessage, setTransmissionMessage] = useState('');

  useEffect(() => {
    if (linkedProfile?.playerId) {
      fetchPlayerFragmentData();
    }
  }, [linkedProfile]);

  // Scramble animation effect
  useEffect(() => {
    const targetText = `AWAITING INPUT... ${fragmentsRedeemed}/${totalFragments} FRAGMENTS`;
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?/~`';
    let iteration = 0;

    const interval = setInterval(() => {
      setDisplayText(prevText => {
        return targetText
          .split('')
          .map((char, index) => {
            if (index < iteration) {
              return targetText[index];
            }
            if (char === ' ') {
              return ' ';
            }
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join('');
      });

      if (iteration >= targetText.length) {
        clearInterval(interval);
      }

      iteration += 1 / 3;
    }, 30);

    return () => clearInterval(interval);
  }, [fragmentsRedeemed, totalFragments]);

  const fetchPlayerFragmentData = async () => {
    try {
      setLoading(true);
      const playerId = linkedProfile.playerId;

      // Fetch player's fragment redemption progress
      const playerData = await PlayerService.getPlayerData(playerId);

      if (playerData?.data) {
        setFragmentsRedeemed(playerData.data.fragmentsRedeemed || 0);
        setUnlockedFiles(playerData.data.unlockedFiles || {});
      }

      // Fetch pending fragments for this player from /neuranet/pending_fragments/{playerId}
      const fragments = await PlayerService.getPendingFragments(playerId);
      setPendingFragments(fragments || []);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching fragment data:', error);
      NotificationManager.show('Failed to load fragment data', 'error');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fragmentKey.trim()) {
      NotificationManager.show('Please enter a fragment key', 'error');
      audioManager.playFail();
      return;
    }

    if (!linkedProfile?.playerId) {
      NotificationManager.show('No linked account found', 'error');
      audioManager.playFail();
      return;
    }

    setLoading(true);

    // Clear input early
    const inputKey = fragmentKey;
    setFragmentKey('');

    try {
      // Normalize the input key (lowercase, trim) - keys in DB are lowercase like "sel_a_4529"
      const normalizedKey = inputKey.toLowerCase().trim();

      console.log('Attempting to redeem key:', normalizedKey);
      console.log('Player ID:', linkedProfile.playerId);

      // Fetch fresh pending fragments to ensure we have the latest data
      const freshPendingFragments = await PlayerService.getPendingFragments(linkedProfile.playerId);
      console.log('Fresh pending fragments:', freshPendingFragments);

      // Log the actual key values for debugging
      freshPendingFragments.forEach((frag, idx) => {
        console.log(`Fragment ${idx} key: "${frag.key}" (type: ${typeof frag.key})`);
      });

      // Fetch fresh player data to get latest unlockedFiles
      const freshPlayerData = await PlayerService.getPlayerData(linkedProfile.playerId);
      const freshUnlockedFiles = freshPlayerData?.data?.unlockedFiles || {};
      const freshFragmentsRedeemed = freshPlayerData?.data?.fragmentsRedeemed || 0;
      console.log('Fresh unlocked files:', freshUnlockedFiles);

      // Update local state with fresh data
      setUnlockedFiles(freshUnlockedFiles);
      setFragmentsRedeemed(freshFragmentsRedeemed);

      // Check if key exists in pending fragments for this user
      // pendingFragments structure: { fragmentId, key, unlocks, generated }
      const matchingFragment = freshPendingFragments.find(fragment => fragment.key === normalizedKey);

      console.log('Matching fragment found:', matchingFragment);

      if (!matchingFragment) {
        // No pending fragment found - check if it's a folder password
        console.log('No pending fragment, checking folder passwords...');
        const folderPassword = await PlayerService.getFolderPassword(normalizedKey);

        if (folderPassword) {
          console.log('Folder password found:', folderPassword);

          // Try to redeem the folder password
          try {
            const unlockedFilesList = await PlayerService.redeemFolderPassword(
              linkedProfile.playerId,
              normalizedKey,
              folderPassword
            );

            // Run defrag animation for first file, show all in success
            await runDefragAnimation(unlockedFilesList.join(', '));

            // Update local state
            const newUnlockedFiles = { ...unlockedFiles };
            unlockedFilesList.forEach(file => {
              newUnlockedFiles[file] = true;
            });
            setUnlockedFiles(newUnlockedFiles);

            // Show transmission modal if there's a msg
            if (folderPassword.msg) {
              setTransmissionMessage(folderPassword.msg);
              setTimeout(() => {
                setShowTransmissionModal(true);
              }, 500);
            }

            // Refresh data
            await fetchPlayerFragmentData();
            setLoading(false);
            return;
          } catch (folderError) {
            await runFalseDefragAnimation();
            setErrorMessage(folderError.message || 'FAILED TO UNLOCK FILES');
            setShowErrorModal(true);
            audioManager.playFail();
            setLoading(false);
            return;
          }
        }

        // Neither pending fragment nor folder password found
        await runFalseDefragAnimation();
        console.log('Invalid key - no matching fragment or folder password found');
        setErrorMessage('INVALID FRAGMENT KEY');
        setShowErrorModal(true);
        audioManager.playFail();
        setLoading(false);
        return;
      }

      // Check if file is already unlocked (using fresh data)
      if (freshUnlockedFiles[matchingFragment.unlocks] === true) {
        // Run false defrag animation for already redeemed
        await runFalseDefragAnimation();
        setErrorMessage('FRAGMENT ALREADY REDEEMED');
        setShowErrorModal(true);
        audioManager.playFail();
        setLoading(false);
        return;
      }

      // Redeem the fragment (validates prerequisites server-side)
      const playerId = linkedProfile.playerId;

      try {
        await PlayerService.redeemFragment(
          playerId,
          matchingFragment.unlocks,  // fileToUnlock (e.g., "selenix_journal_fragment_01")
          normalizedKey,              // fragmentKey
          matchingFragment.fragmentId // fragmentId for deletion from pending_fragments
        );
      } catch (redeemError) {
        // Handle prerequisite validation errors
        await runFalseDefragAnimation();
        setErrorMessage(redeemError.message || 'FAILED TO REDEEM FRAGMENT');
        setShowErrorModal(true);
        audioManager.playFail();
        setLoading(false);
        return;
      }

      // Valid key and prerequisites met - run the full defrag animation
      await runDefragAnimation(matchingFragment.unlocks);

      // Update local state
      setFragmentsRedeemed(prev => prev + 1);
      setUnlockedFiles(prev => ({ ...prev, [matchingFragment.unlocks]: true }));

      // Show transmission modal if fragment has a message
      if (matchingFragment.msg) {
        setTransmissionMessage(matchingFragment.msg);
        setTimeout(() => {
          setShowTransmissionModal(true);
        }, 500);
      }

      // Refresh data
      await fetchPlayerFragmentData();
    } catch (error) {
      console.error('Error redeeming fragment:', error);
      setErrorMessage('FAILED TO REDEEM FRAGMENT');
      setShowErrorModal(true);
      audioManager.playFail();
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    audioManager.playSelect();
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    audioManager.playSelect();
  };

  const handleCloseTransmissionModal = () => {
    setShowTransmissionModal(false);
    audioManager.playSelect();
  };

  // False defragmentation process (runs first)
  const runFalseDefragAnimation = async () => {
    setIsDefragging(true);
    setDefragProgress(0);

    const falseStatuses = [
      'RECEIVING FRAGMENT INPUT...',
      'PARSING FRAGMENT KEY...',
      'ESTABLISHING SECURE CONNECTION...',
      'AUTHENTICATING CREDENTIALS...',
      'DECRYPTING FRAGMENT DATA...'
    ];

    for (let i = 0; i < falseStatuses.length; i++) {
      setDefragStatus(falseStatuses[i]);
      setDefragProgress(((i + 1) / falseStatuses.length) * 100);

      // Random delay between 300-700ms for each step
      await new Promise(resolve => setTimeout(resolve, Math.random() * 400 + 300));

      // Play subtle sound effects
      audioManager.playHover().catch(() => {});
    }

    // Brief pause before hiding
    await new Promise(resolve => setTimeout(resolve, 200));
    setIsDefragging(false);
  };

  // Real defragmentation animation sequence (runs on success)
  const runDefragAnimation = async (filePath) => {
    setIsDefragging(true);
    setDefragProgress(0);

    const statuses = [
      'INITIALIZING DEFRAGMENTATION...',
      'SCANNING FRAGMENT STRUCTURE...',
      'ANALYZING DATA INTEGRITY...',
      'RECONSTRUCTING FILE SECTORS...',
      'VALIDATING CHECKSUMS...',
      'OPTIMIZING MEMORY ALLOCATION...',
      'FINALIZING DEFRAGMENTATION...',
      'COMPLETE'
    ];

    for (let i = 0; i < statuses.length; i++) {
      setDefragStatus(statuses[i]);
      setDefragProgress(((i + 1) / statuses.length) * 100);

      // Random delay between 200-600ms for each step
      await new Promise(resolve => setTimeout(resolve, Math.random() * 400 + 200));

      // Play subtle sound effects
      if (i < statuses.length - 1) {
        audioManager.playSelect().catch(() => {});
      }
    }

    // Final success
    await new Promise(resolve => setTimeout(resolve, 300));
    setIsDefragging(false);
    setUnlockedFilePath(filePath);
    setShowSuccessModal(true);
    audioManager.playSuccess();
  };

  // Test function for dev environment
  const handleTestDefrag = () => {
    setFragmentsRedeemed(prev => prev + 1);
    runDefragAnimation('test/file/path_' + Date.now());
  };

  // Test error modal
  const handleTestError = async () => {
    setLoading(true);
    await runFalseDefragAnimation();
    await new Promise(resolve => setTimeout(resolve, 100));
    setErrorMessage('INVALID FRAGMENT KEY');
    setShowErrorModal(true);
    audioManager.playFail();
    setLoading(false);
  };

  return (
    <div className="fragment-redemption-module">
      <div className="module-header">
        <button onClick={onBack} className="back-button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          BACK
        </button>
        <h1>DEFRAGGER</h1>
      </div>

      <div className="redemption-container">
        <div className="status-display">
          <div className="status-line system-line">
            [SYSTEM:VAULT_ACCESS]
          </div>
          <div className="status-line main-status">
            {displayText}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="redemption-form">
          <div className="input-group">
            <label htmlFor="fragment-key">ENTER FRAGMENT KEY:</label>
            <input
              id="fragment-key"
              type="text"
              value={fragmentKey}
              onChange={(e) => setFragmentKey(e.target.value)}
              placeholder="[XXXX-XXXX-XXXX]"
              className="fragment-input"
              disabled={loading}
              autoComplete="off"
              spellCheck="false"
            />
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={loading || !fragmentKey.trim()}
          >
            {loading ? 'PROCESSING...' : 'SUBMIT'}
          </button>
        </form>

        {/* Defrag Animation Overlay */}
        {isDefragging && (
          <div className="defrag-overlay">
            <div className="defrag-container">
              <div className="defrag-title">DEFRAGMENTATION IN PROGRESS</div>
              <div className="defrag-progress-bar">
                <div
                  className="defrag-progress-fill"
                  style={{ width: `${defragProgress}%` }}
                />
              </div>
              <div className="defrag-percentage">{Math.floor(defragProgress)}%</div>
              <div className="defrag-status">{defragStatus}</div>
              <div className="defrag-spinner">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="defrag-dot" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal-backdrop" onClick={handleCloseSuccessModal} />
          <div className="success-modal-container">
            <div className="success-modal-header">
              <h2>[ACCESS GRANTED]</h2>
              <button onClick={handleCloseSuccessModal} className="modal-close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="success-modal-content">
              <div className="success-icon">
                <div className="lock-mechanism">
                  <div className="lock-ring lock-ring-outer"></div>
                  <div className="lock-ring lock-ring-middle"></div>
                  <div className="lock-ring lock-ring-inner"></div>
                  <div className="lock-segments">
                    <div className="lock-segment"></div>
                    <div className="lock-segment"></div>
                    <div className="lock-segment"></div>
                    <div className="lock-segment"></div>
                  </div>
                  <div className="lock-core">
                    <div className="lock-core-inner"></div>
                  </div>
                </div>
              </div>
              <div className="success-message">
                DEFRAGMENTATION COMPLETE
              </div>
              <div className="success-detail">
                {unlockedFilePath.split(', ').map((f, index) => {
                  const info = getDisplayInfo(f.trim());
                  return (
                    <div key={index} className="file-unlock-display">
                      <div className="path-div">
                        <div className="file-path">{info.path}</div>
                      </div>
                      <div className="file-div">
                        <div className="file-name">{info.file}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <button onClick={handleCloseSuccessModal} className="modal-continue-button">
              CONTINUE
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="success-modal-overlay">
          <div className="success-modal-backdrop" onClick={handleCloseErrorModal} />
          <div className="success-modal-container error-modal">
            <div className="success-modal-header error-header">
              <h2>[ACCESS DENIED]</h2>
              <button onClick={handleCloseErrorModal} className="modal-close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="success-modal-content">
              <div className="error-icon">✕</div>
              <div className="error-message">
                {errorMessage}
              </div>
            </div>
            <button onClick={handleCloseErrorModal} className="modal-continue-button">
              CONTINUE
            </button>
          </div>
        </div>
      )}

      {/* Transmission Modal - Dynamic Message */}
      {showTransmissionModal && (
        <div className="success-modal-overlay transmission-overlay">
          <div className="success-modal-backdrop" onClick={handleCloseTransmissionModal} />
          <div className="success-modal-container transmission-modal">
            <div className="success-modal-header transmission-header">
              <h2>[INCOMING TRANSMISSION]</h2>
              <button onClick={handleCloseTransmissionModal} className="modal-close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="success-modal-content transmission-content">
              <div className="transmission-text">
                <p className="transmission-message-raw">{transmissionMessage?.replace(/\\n/g, '\n')}</p>
              </div>
            </div>
            <button onClick={handleCloseTransmissionModal} className="modal-continue-button transmission-button">
              ACKNOWLEDGE
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FragmentRedemption;
